import React, { useEffect, useState } from 'react';
import { Button, Form, Message, Modal } from 'semantic-ui-react';
import {
  buildEditPatches,
  buildInitialForm,
  marketplaceCountryOptions,
  normalizePhoneNumber,
  validateBillingFields,
  validateEmail,
} from './userForm';

const roleOptions = [
  { key: 'user', text: 'Usuario', value: 'USER' },
  { key: 'admin', text: 'Admin', value: 'ADMIN' },
];

/**
 * Edits every admin-managed field of a user. `onSave(patches)` receives
 * `{ profile, billing }` with only the changed fields and must resolve to
 * `{ ok: boolean, message?: string }`.
 */
function EditUserModal({ open, row, isSelf, saving, onClose, onSave }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && row) {
      setForm(buildInitialForm(row));
      setError('');
    }
  }, [open, row]);

  if (!open || !row || !form) return null;

  // Payment data and the marketplace country belong to the owner account.
  const isEmployee = Boolean(row.ownerUserId);

  const setField = (name) => (_, { value, checked }) =>
    setForm((prev) => ({ ...prev, [name]: checked !== undefined ? checked : value }));

  const handleSave = async () => {
    const validationError = validateEmail(form.email) || validateBillingFields(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    const patches = buildEditPatches(row, form);
    if (Object.keys(patches.profile).length === 0 && Object.keys(patches.billing).length === 0) {
      onClose();
      return;
    }
    const result = await onSave(patches);
    if (result?.ok) {
      onClose();
    } else {
      setError(result?.message || 'No se pudieron guardar los cambios.');
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose} size="small">
      <Modal.Header>Editar usuario: {row.username}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Email"
            value={form.email}
            onChange={setField('email')}
            disabled={saving}
          />
          <Form.Group widths="equal">
            <Form.Select
              label="Rol"
              options={roleOptions}
              value={form.role}
              onChange={setField('role')}
              disabled={saving || isSelf}
            />
            <Form.Checkbox
              toggle
              label={form.isActive ? 'Usuario activo' : 'Usuario inactivo (no puede iniciar sesión)'}
              checked={form.isActive}
              onChange={setField('isActive')}
              disabled={saving || isSelf}
              style={{ marginTop: 28 }}
            />
          </Form.Group>
          <Form.Select
            label="País de Mercado Libre"
            options={marketplaceCountryOptions}
            value={form.marketplaceCountry}
            onChange={setField('marketplaceCountry')}
            disabled={saving || isEmployee}
          />
          {isSelf ? (
            <Message info size="small">
              No podés cambiar el rol ni desactivar tu propia cuenta.
            </Message>
          ) : null}
          {isEmployee ? (
            <Message info size="small">
              Los datos de pago y el país de Mercado Libre se definen en la cuenta dueña{row.ownerUsername ? ` (${row.ownerUsername})` : ''}.
            </Message>
          ) : (
            <>
            <Form.Input
              label="Teléfono de WhatsApp (solo números, con código de país)"
              placeholder="5491122334455"
                value={form.phoneNumber}
                onChange={setField('phoneNumber')}
                onBlur={(_, { value }) => setForm((prev) => ({ ...prev, phoneNumber: normalizePhoneNumber(value) }))}
              disabled={saving}
            />
            <Form.Group widths="equal">
              <Form.Input
                label="Monto a pagar"
                type="number"
                min="0"
                step="0.01"
                value={form.nextPaymentAmount}
                onChange={setField('nextPaymentAmount')}
                disabled={saving}
              />
              <Form.Input
                label="Vencimiento"
                type="date"
                value={form.nextPaymentDueDate}
                onChange={setField('nextPaymentDueDate')}
                disabled={saving}
              />
            </Form.Group>
            </>
          )}
        </Form>
        {error ? (
          <Message negative size="small">
            {error}
          </Message>
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="purple" onClick={handleSave} loading={saving} disabled={saving}>
          Guardar
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default EditUserModal;
